import React from "react"
import { BodyText, InlineText, SubTitle } from "../../Typography/Typography"
import SmartLink from "../../layout/SmartLink/SmartLink"

const providerProfileClickEvent = () => ({
  name: "SEO Filtered Directory - Copy link to provider clicked",
  properties: {
    "Care Type": "care_home",
    "GEO Location": "Eastbourne",
    "Current URL":
      "https://www.caresourcer.com/s/providers/towns/eastbourne/care-home/",
  },
})

export default {
  summary: (
    <BodyText marginBottom>
      Choosing a care home for a loved one can be difficult, however with 52
      care homes in Eastbourne, and 24 offering specialist dementia care, it is
      important you make the right decision on which care home is best for your
      loved one’s needs. Here are a few of our top tips to help you narrow your
      options.
    </BodyText>
  ),
  content: [
    <SubTitle marginBottom bold>
      Location is key
    </SubTitle>,
    <BodyText marginBottom>
      As well as thinking about transport links and ensuring friends and family
      are able to visit, it is also important to consider where in Eastbourne
      your loved one would be most happy to live. With so many care homes to
      consider you can afford to be a little picky. For example, how about{" "}
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-7574260323/mortain-place-care-home"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Mortain Place Care Home</InlineText>
      </SmartLink>
      , which is located opposite the marina and would allow your loved one to
      watch the boats sailing past whilst having their evening meal. If
      something in a more residential area would suit better, then check out{" "}
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-140550665/inglewood-nursing-home"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Inglewood Nursing Home</InlineText>
      </SmartLink>{" "}
      with some lovely gardens and only a few minutes walk to Hampden Park for
      an afternoon stroll.
    </BodyText>,
    <SubTitle marginBottom bold>
      Feeling at home
    </SubTitle>,
    <BodyText marginBottom>
      As Eastbourne is a small town, it is important to think about the
      lifestyle that would be offered to your loved one in the care home, and to
      find one that would help them feel most at home. Below are a few things we
      recommend you consider when narrowing down your options:
    </BodyText>,
    <SubTitle marginBottom bold italic>
      Gardens and outdoor spaces
    </SubTitle>,
    <BodyText marginBottom>
      Most care homes in Eastbourne have a garden, however they can vary a lot
      in size and functionality, which makes it important to understand how
      important having the outdoor space is to your loved one. Would they be
      happy with a lovely seating area where they can spend time with family and
      breathe in the fresh sea air? Or do they prefer to help potter in the
      garden? If so, perhaps{" "}
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-3110978392/elstree-court-care-home"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Elstree Court Care Home</InlineText>
      </SmartLink>{" "}
      could be well suited.
    </BodyText>,
    <BodyText marginBottom bold italic>
      Pets welcome
    </BodyText>,
    <BodyText marginBottom>
      Did you know that some care homes in Eastbourne will let your loved one’s
      pet visit them? Moving into a home and having to leave your pet with
      friends or family can be upsetting, so perhaps knowing they can still see
      them is important. If it is, we recommend asking care homes about their
      pet policy, because like{" "}
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-109522638/bendigo-nursing-home"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Bendigo Nursing Home</InlineText>
      </SmartLink>{" "}
      in central Eastbourne, they may let pets visit upon appointment.
    </BodyText>,
    <BodyText marginBottom bold italic>
      Activities and days out
    </BodyText>,
    <BodyText marginBottom>
      To help residents feel at home and continue their hobbies, most care homes
      in Eastbourne offer a wide range of activities and facilities. Many even
      encourage residents to share and teach others their talents, whether it be
      baking, art or dancing. When possible care homes also try to organise
      small outings for their residents to Royal Hippodrome Theatre, walks along
      Beachy Head to take in fresh air and see the white chalk cliffs, or to
      simply have fish and chips overlooking the Eastbourne Marina. Care homes
      are generally very accommodating so if there is something your loved one
      likes to do, ensure you ask if it would be possible when staying at the
      home.
    </BodyText>,
  ],
}
